import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Container from '../components/ui/Container';
import NotFoundPage from '../components/NotFoundPage';
import OptimizedImage from '../components/ui/OptimizedImage';
import InternalLinks from '../components/InternalLinks';
import { calculateReadingTime, formatReadingTime, getWordCount } from '../utils/readingTime';
import './BlogPostPage.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [postMeta, setPostMeta] = useState(null);
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError('');
    setPostMeta(null);
    setContent('');

    Promise.all([
      fetch('/blog/index.json', { cache: 'no-store' }).then((r) => {
        if (!r.ok) throw new Error('Failed to load blog index');
        return r.json();
      }),
      fetch(`/blog/${slug}.md`, { cache: 'no-store' }).then((r) => {
        if (!r.ok) throw new Error('Post not found');
        return r.text();
      })
    ])
      .then(([index, md]) => {
        if (!isMounted) return;
        const meta = index.find((p) => p.slug === slug);
        if (!meta) throw new Error('Post metadata missing');
        setPostMeta(meta);
        setContent(md);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message || 'Error loading post');
      })
      .finally(() => isMounted && setIsLoading(false));

    return () => { isMounted = false; };
  }, [slug]);

  if (!isLoading && (error || !postMeta)) {
    return <NotFoundPage />;
  }

  const readingTime = content ? calculateReadingTime(content) : 0;
  const wordCount = content ? getWordCount(content) : 0;

  // OG/Twitter images must be absolute URLs; prefer the JPEG cover variant
  // (broadest scraper support — the generator emits .jpg alongside .webp).
  const ogImage = postMeta?.image
    ? `https://pysolutions.dev${postMeta.image.replace(/\.webp$/, '.jpg')}`
    : 'https://pysolutions.dev/og-card.jpg';

  const structuredData = postMeta ? {
    '@context': 'https://schema.org',
    '@type': ['BlogPosting', 'Article'],
    headline: postMeta.title,
    description: postMeta.description,
    image: ogImage,
    author: { '@type': 'Organization', name: postMeta.author || 'PySolutions' },
    publisher: {
      '@type': 'Organization',
      name: 'PySolutions',
      logo: { '@type': 'ImageObject', url: 'https://pysolutions.dev/PySolutions.jpeg' }
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://pysolutions.dev/blog/${postMeta.slug}` },
    datePublished: postMeta.date,
    dateModified: postMeta.updatedAt || postMeta.date,
    wordCount: wordCount,
    timeRequired: `PT${readingTime}M`,
    articleSection: postMeta.tags && postMeta.tags[0] ? postMeta.tags[0] : 'Technology',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', '.post-subtitle']
    },
    keywords: postMeta.tags ? postMeta.tags.join(', ') : undefined
  } : null;

  const isLogoImage = !!(postMeta?.image && /PySolutions\.(?:jpeg|jpg|png)$/i.test(postMeta.image));

  return (
    <div className="blog-post-page page-with-header">
      {postMeta && (
        <Helmet>
          <title>{postMeta.title} - PySolutions Blog</title>
          <meta name="description" content={postMeta.description} />
          <link rel="canonical" href={`https://pysolutions.dev/blog/${postMeta.slug}`} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={`https://pysolutions.dev/blog/${postMeta.slug}`} />
          <meta property="og:title" content={`${postMeta.title} - PySolutions Blog`} />
          <meta property="og:description" content={postMeta.description} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={postMeta.title} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${postMeta.title} - PySolutions Blog`} />
          <meta name="twitter:description" content={postMeta.description} />
          <meta name="twitter:image" content={ogImage} />
          {structuredData && (
            <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
          )}
        </Helmet>
      )}

      <section className="post-hero section">
        <div className="post-hero-background">
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
        </div>
        <Container>
          {isLoading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            postMeta && (
              <div className="post-hero-content">
                <div className="hero-badge"><span className="badge-icon">📖</span> Article</div>
                <h1 className="post-title">{postMeta.title}</h1>
                <p className="post-subtitle">{postMeta.description}</p>
                <div className="post-meta">
                  <span>{new Date(postMeta.date).toLocaleDateString()}</span>
                  {postMeta.author && <span>• {postMeta.author}</span>}
                  {readingTime > 0 && <span>• {formatReadingTime(readingTime)}</span>}
                  {wordCount > 0 && <span>• {wordCount.toLocaleString()} words</span>}
                </div>
              </div>
            )
          )}
        </Container>
      </section>

      {!isLoading && postMeta && (
        <section className="post-content section">
          <Container>
            {postMeta.image && !isLogoImage && (
              <div className={`post-cover ${isLogoImage ? 'is-logo' : ''}`}>
                <OptimizedImage 
                  className={isLogoImage ? 'logo' : ''} 
                  src={postMeta.image} 
                  alt={`${postMeta.title} - Python development tutorial showing practical code examples and best practices`}
                  width="800"
                  height="450"
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, 800px"
                />
              </div>
            )}
            <article className="markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </article>
            
            {/* Enhanced Internal Linking */}
            {postMeta && (
              <InternalLinks 
                currentSlug={postMeta.slug}
                currentTags={postMeta.tags}
                title="Related Articles"
                maxLinks={3}
              />
            )}
          </Container>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage; 